import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Pagination,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  ShoppingCart,
  CheckCircle,
  Pending,
  Inventory,
  PlaylistAddCheck,
} from "@mui/icons-material";
import { purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';
export const InvPurchase = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPurchaseOrders();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await purchaseAPI.getAll(params);
      const { purchaseOrders: ordersData, pagination: paginationData } =
        response.data.data;

      setPurchaseOrders(ordersData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching purchase orders", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't set selectedOrder to null here, keep it for dialogs
  };

  const handleView = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}`);
      setSelectedOrder(null); 
    }
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}/edit`);
      setSelectedOrder(null);
    }
    setAnchorEl(null);
  };

  const handleReceive = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}/receive`);
      handleMenuClose();
    }
  };

  const handlePartialReceive = () => {
    if (selectedOrder) {
      navigate(`/hospital/hospital-inventory/purchases/${selectedOrder._id}/receive?partial=true`);
      handleMenuClose();
    }
  };

  const handleStatusUpdate = () => {
    if (selectedOrder) {
      setSelectedStatus(selectedOrder.status);
      setStatusDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    if (selectedOrder) {
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder || !selectedOrder._id) {
      enqueueSnackbar("No order selected", { variant: "error" });
      return;
    }

    try {
      await purchaseAPI.updateStatus(selectedOrder._id, {
        status: selectedStatus,
      });
      enqueueSnackbar("Order status updated successfully", {
        variant: "success",
      });
      setStatusDialogOpen(false);
      setSelectedOrder(null); // Clear after operation
      fetchPurchaseOrders(); // Refresh the list
    } catch (error) {
      enqueueSnackbar("Error updating order status", { variant: "error" });
    }
  };

  const deleteOrder = async () => {
    if (!selectedOrder || !selectedOrder._id) {
      enqueueSnackbar("No order selected", { variant: "error" });
      return;
    }

    try {
      await purchaseAPI.delete(selectedOrder._id);
      enqueueSnackbar("Purchase order deleted successfully", {
        variant: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedOrder(null); // Clear after operation
      fetchPurchaseOrders(); // Refresh the list
    } catch (error) {
      enqueueSnackbar("Error deleting purchase order", { variant: "error" });
    }
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSelectedOrder(null); // Clear when dialog closes without action
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedOrder(null); // Clear when dialog closes without action
  };

  const canEditOrder = (order) => {
    return order && ["draft", "sent"].includes(order.status);
  };

  const canDeleteOrder = (order) => {
    return order && order.status === "draft";
  };

  const canReceiveOrder = (order) => {
    return (
      order &&
      ["confirmed", "sent", "partially_received"].includes(order.status)
    );
  };

  const canPartiallyReceiveOrder = (order) => {
    return (
      order &&
      ["confirmed", "sent", "partially_received"].includes(order.status)
    );
  };

  const canUpdateStatus = (order) => {
    return (
      order && order.status !== "completed" && order.status !== "cancelled"
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      sent: "info",
      confirmed: "primary",
      partially_received: "warning",
      completed: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircle />,
      confirmed: <CheckCircle />,
      partially_received: <PlaylistAddCheck />,
      sent: <Pending />,
      draft: <Pending />,
      cancelled: <Pending />,
    };
    return icons[status] || <Pending />;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Purchase Orders
        </Typography>
        {hasAnyRole(["admin", "inventory_manager"]) && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/hospital/hospital-inventory/purchases/new")}
          >
            New Purchase Order
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <ShoppingCart color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {pagination.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {purchaseOrders.filter((o) => o.status === "completed").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {
                  purchaseOrders.filter(
                    (o) => o.status === "partially_received"
                  ).length
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Partial Received
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {
                  purchaseOrders.filter((o) =>
                    ["sent", "confirmed"].includes(o.status)
                  ).length
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="default">
                {purchaseOrders.filter((o) => o.status === "draft").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Draft
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by PO number or supplier name..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" align="center">
                Total: {pagination.total} orders
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Purchase Orders
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Order Date</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrders.map((order) => (
                      <TableRow key={order._id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {order.poNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.supplier.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.supplier.contactPerson}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(order.orderDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${order.items.length} items`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            ₹{order.grandTotal?.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={getStatusIcon(order.status)}
                            label={order.status.replace("_", " ")}
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, order)}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {purchaseOrders.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <ShoppingCart
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No purchase orders found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {searchTerm
                      ? "Try adjusting your search terms"
                      : "Get started by creating your first purchase order"}
                  </Typography>
                  {hasAnyRole(["admin", "inventory_manager"]) && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate("/hospital/hospital-inventory/purchases/new")}
                    >
                      Create First PO
                    </Button>
                  )}
                </Box>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Pagination
                    count={pagination.pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleView}>
          <Visibility sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleEdit}
            disabled={!canEditOrder(selectedOrder)}
          >
            <Edit sx={{ mr: 1, fontSize: 20 }} />
            Edit Order
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleReceive}
            disabled={!canReceiveOrder(selectedOrder)}
          >
            <CheckCircle sx={{ mr: 1, fontSize: 20 }} />
            Receive Complete
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handlePartialReceive}
            disabled={!canPartiallyReceiveOrder(selectedOrder)}
          >
            <PlaylistAddCheck sx={{ mr: 1, fontSize: 20 }} />
            Receive Partial
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleStatusUpdate}
            disabled={!canUpdateStatus(selectedOrder)}
          >
            <Inventory sx={{ mr: 1, fontSize: 20 }} />
            Update Status
          </MenuItem>
        )}

        {hasAnyRole(["admin", "inventory_manager"]) && (
          <MenuItem
            onClick={handleDelete}
            disabled={!canDeleteOrder(selectedOrder)}
            sx={{ color: "error.main" }}
          >
            <Delete sx={{ mr: 1, fontSize: 20 }} />
            Delete Order
          </MenuItem>
        )}
      </Menu>

      {/* Update Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            PO: {selectedOrder?.poNumber}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          {selectedStatus === "completed" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Note: Marking as completed will require receiving the goods
              through the receive process.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusDialogClose}>Cancel</Button>
          <Button
            onClick={updateOrderStatus}
            variant="contained"
            disabled={
              !selectedStatus ||
              !selectedOrder ||
              selectedStatus === selectedOrder?.status
            }
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Purchase Order</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. This will permanently delete the
            purchase order.
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete purchase order{" "}
            <strong>{selectedOrder?.poNumber}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Supplier: {selectedOrder?.supplier?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Amount: ₹{selectedOrder?.grandTotal?.toFixed(2)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button
            onClick={deleteOrder}
            variant="contained"
            color="error"
            disabled={!selectedOrder}
          >
            Delete Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
